import React from "react";
import { Table, Typography, Tag, Popover, Tooltip } from "antd";
import styled from "styled-components";
import { FormattedMessage as IntlMessage } from "react-intl";
import Employee from "./tableItems/Employee";
import EmployeeTypes from "../tables/EmployeeTypes";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import EmployeeSchedulingTable from "./EmployeeSchedulingTable";
import moment from "moment";
import { CustomerServiceOutlined } from "@ant-design/icons";
const { Text } = Typography;

const DistanceGradeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const DistanceGrade = ({ record }) => {
  const distance = record.distance;
  const hasDistance = distance != null && distance < 500;

  return (
    <DistanceGradeWrapper>
      <Text>{record.distance_grade + "%"}</Text>
      {hasDistance && (
        <>
          <Text style={{ padding: "0px 5px" }}>({record.distance}</Text>
          <IntlMessage id="app.employees.distanceMeasure" />
          <Text>)</Text>
        </>
      )}
    </DistanceGradeWrapper>
  );
};

const EmployeesTable = ({
  employees,
  onChosenEmployee,
  activeEvent,
  pagination,
  onChange,
  type,
  originalEmployeeId,
}) => {
  const intl = useIntl();
  const loading = useSelector(
    (state) => state.pendingActions["scheduling/loadEmployeesMatching"]
  );

  const getTooltipContent = (record) => {
    let nonAvailableMessage = "";
    if (record.not_available && record.not_avilable_slots) {
      const slots = record.not_avilable_slots.map(
        (i) =>
          `${moment.weekdays(true)[i.day_of_week]} ${moment()
            .startOf("day")
            .set("minute", i.start_time_minutes)
            .format("HH:mm")}:${moment()
            .startOf("day")
            .set("minute", i.start_time_minutes + i.duration_minutes)
            .format("HH:mm")} `
      );

      nonAvailableMessage = `${intl.formatMessage({
        id: `app.employees.not_available`,
      })}: ${slots.join(", ")}`;
    }

    const hasConflicts =
      (record.event_conflicts ||
        record.not_available ||
        record.restricted_age ||
        (type === "scheduling" && record.absent)) &&
      record.id !== originalEmployeeId;

    const content = (
      <div>
        {record.event_conflicts && (
          <div>
            <EmployeeSchedulingTable
              startDate={moment(activeEvent.start).startOf("day").format()}
              endDate={moment(activeEvent.start).endOf("day").format()}
              employee={record}
              type={type}
              recurrent_event_conflicts={
                type === "plan" && record.recurrent_event_conflicts
              }
            />
          </div>
        )}

        {record.event_conflicts && (
          <div style={{ paddingTop: "10px" }}>
            <Text style={{ color: "red" }}>
              {intl.formatMessage({ id: `app.employees.event_conflicts` })}
            </Text>
          </div>
        )}
        {record.not_available && (
          <Text style={{ color: "red" }}>{nonAvailableMessage}</Text>
        )}
        {record.restricted_age && (
          <Text style={{ color: "red" }}>
            {intl.formatMessage({ id: `app.employees.restricted_age` })}
          </Text>
        )}
        {type === "scheduling" && record.absent && (
          <Text style={{ color: "red" }}>
            {intl.formatMessage({ id: `app.employees.absent` })}
          </Text>
        )}
      </div>
    );

    return hasConflicts ? (
      <Popover
        placement="top"
        title={null}
        trigger="hover"
        destroyTooltipOnHide
        content={content}
      >
        <Tag color="red">
          <IntlMessage id="app.employees.notAvailable" />
        </Tag>
      </Popover>
    ) : (
      <Tag color="cyan">
        <IntlMessage id="app.employees.available" />
      </Tag>
    );
  };

  const columns = [
    {
      title: <IntlMessage id="app.employees.fullname" />,
      dataIndex: "first_name",
      width: "200px",
      render: (text, record) => (
        <Tooltip title={record.local_id_number}>
          <div>
            <Employee record={record} text={text} />
          </div>
        </Tooltip>
      ),
    },
    {
      title: <IntlMessage id="app.employees.employee_type_id" />,
      dataIndex: "employee_type_id",
      render: (text, record) => (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <EmployeeTypes text={text} />{" "}
          {record.oncall && (
            <Tooltip
              title={intl.formatMessage({
                id: "app.scheduling.table.oncall_hours",
              })}
            >
              <CustomerServiceOutlined />
            </Tooltip>
          )}
        </div>
      ),
      width: "140px",
    },
    {
      title: <IntlMessage id="app.employees.availabilityColumn" />,
      dataIndex: "event_conflicts",
      width: "80px",
      render: (_text, record) => getTooltipContent(record),
    },
    {
      title: <IntlMessage id="app.employees.distanceScore" />,
      dataIndex: "distance_grade",
      width: "130px",
      responsive: ["xl"],
      sorter: true,
      render: (text, record) => <DistanceGrade record={record} />,
    },
    {
      title: <IntlMessage id="app.employees.classificationScore" />,
      dataIndex: "classification_grade",
      width: "130px",
      responsive: ["xl"],
      sorter: true,
      render: (text) => <Text>{text + "%"}</Text>,
    },
    {
      title: <IntlMessage id="app.employees.totalScore" />,
      dataIndex: "total_grade",
      width: "130px",
      sorter: true,
      render: (text) => <Text>{text + "%"}</Text>,
    },
  ];

  const rowSelection = {
    selectedRowKeys:
      activeEvent && activeEvent.employee_id ? [activeEvent.employee_id] : [],
    onChange: (selectedRowKeys, selectedRows) => {
      onChosenEmployee(selectedRows[0]);
    },
    getCheckboxProps: (record) => ({
      disabled:
        type !== "scheduling" &&
        type !== "replacements" &&
        record.id !== originalEmployeeId &&
        (record.event_conflicts ||
          record.restricted_age ||
          record.not_available ||
          (type === "scheduling" && record.absent)),
    }),
  };

  return (
    <Table
      bordered
      rowSelection={{
        type: "radio",
        ...rowSelection,
      }}
      columns={columns}
      dataSource={employees}
      rowKey={(record) => record.id}
      style={{ height: window.innerHeight - 442 }}
      scroll={{ y: window.innerHeight - 546 }}
      loading={loading}
      pagination={pagination}
      onChange={onChange}
    />
  );
};

export default EmployeesTable;
